import { filters as A11Y_FILTERS, svgDefs as A11Y_SVG_DEFS } from '@symbo.ls/brand/designSystem/a11y.js'

const DEVICES = {
  screenL: [1920, 1024],
  screenM: [1680, 1024],
  tabletL: [1366, 926],
  tabletS: [1024, 768],
  mobileXS: [375, 768]
}

export const Scene = {
  extends: 'SceneTemplate',
  margin: '- auto',
  boxSize: '100%',
  position: 'relative',
  align: 'center center',
  padding: 0,

  A11yFilterDefs: {
    tag: 'svg',
    width: '0',
    height: '0',
    position: 'absolute',
    html: A11Y_SVG_DEFS
  },

  IframeContainer: {
    // Outer clipping box wraps BOTH the app iframe and the design-system
    // iframe so device presets (previewSize) scale whichever is visible. Parent `Scene` is `align: center center` so the
    // container sits in the middle of the preview area. We size it to the
    // VISUAL (post-scale) footprint so it can never exceed the parent, no
    // matter which device preset is active. The inner `AppIframe` then
    // renders at the FULL device resolution and scales down via `transform`
    // so the embedded app's own media queries see the real device width
    // (e.g. 1920 for ScreenL, 375 for MobileXS) while the on-screen box
    // never overflows the preview.
    position: 'relative',
    flexShrink: '0',
    overflow: 'hidden',
    transition: 'A2 defaultBezier',
    transitionProperty: 'max-width, max-height, width, height, transform',

    style: (el, s) => {
      const size = s.previewSize
      const a11y = s.a11yFilter ? A11Y_FILTERS[s.a11yFilter] : null
      const filterStyle = { filter: a11y || 'none' }

      if (!size) return { width: '100%', height: '100%', ...filterStyle }
      if (size === 'resizable')
        return {
          width: '85%',
          height: '75%',
          resize: 'both',
          transition: 'none',
          ...filterStyle
        }
      const breakpoint = DEVICES[size]
      if (!breakpoint) return { width: '100%', height: '100%', ...filterStyle }
      const [w, h] = breakpoint
      const parent = el.node?.parentElement
      const availW = parent ? parent.clientWidth : 1920
      const availH = parent ? parent.clientHeight : 1080
      const scale = Math.min(availW / w, availH / h, 1)
      return {
        width: Math.round(w * scale) + 'px',
        height: Math.round(h * scale) + 'px',
        ...filterStyle
      }
    },

    AppIframe: {
      tag: 'iframe',
      id: 'preview-app-iframe',
      border: '0',
      position: 'absolute',
      top: '0',
      left: '0',
      transformOrigin: '0 0',
      display: (_, s) => s.viewMode === 'designSystem' ? 'none' : 'block',
      style: (el, s) => sizeForDevice(el, s),
      allow:
        'accelerometer; camera; encrypted-media; geolocation; gyroscope; hid; microphone; midi; clipboard-write',
    },

    DesignSystemIframe: {
      tag: 'iframe',
      id: 'preview-ds-iframe',
      border: '0',
      position: 'absolute',
      top: '0',
      left: '0',
      transformOrigin: '0 0',
      display: (_, s) => s.viewMode === 'designSystem' ? 'block' : 'none',
      style: (el, s) => sizeForDevice(el, s)
    }
  }
}

function sizeForDevice (el, s) {
  const size = s.previewSize
  if (!size || size === 'resizable') {
    return { width: '100%', height: '100%', transform: 'none' }
  }
  const bp = DEVICES[size]
  if (!bp) return { width: '100%', height: '100%', transform: 'none' }
  const [w, h] = bp
  const outer = el.parent?.node
  const availW = outer?.parentElement ? outer.parentElement.clientWidth : 1920
  const availH = outer?.parentElement ? outer.parentElement.clientHeight : 1080
  const scale = Math.min(availW / w, availH / h, 1)
  return {
    width: w + 'px',
    height: h + 'px',
    transform: scale < 1 ? 'scale(' + scale + ')' : 'none'
  }
}
